// src/components/common/FocusAndLogisticsModal.jsx
//
// 3-question modal that captures user_customer_focus + b_customer_focus +
// b_service_distance — the trio that powers Teams matching. Built for the
// lean-onboarding redesign (2026-05-26):
//   - Slice 4: hard blocker on /teams when any of the 3 is null. The matching
//     algorithm CAN'T recommend partners without all three, so we gate it.
//   - Slice 2b: optional "Update Focus and Logistics" button in the profile
//     editor, where users can change these later (dismissable, no gate).
//
// One component, two modes — `mode="blocker"` hides the X + Cancel and uses
// the blocker copy; `mode="editor"` shows them and uses the editor copy.
//
// Localized via designCenterLocale.js (en/es/ja/th currently).
//
// Backend: PUT /api/profile/update-focus-and-logistics (atomic 3-field update).
// On success, calls onSaved(updatedProfile) so the caller can refresh
// profileStore + close the modal. On error, displays the message inline and
// stays open so the user can retry.

import React, { useState } from 'react';
import { getStrings } from '../../design/designCenterLocale';
import { useProfileStore } from '../../state/profileStore';

const BRAND = '#00aef7';

export default function FocusAndLogisticsModal({
  mode = 'blocker',       // 'blocker' (teams gate) | 'editor' (profile-editor opt-in)
  isOpen,
  onClose,                 // ignored when mode='blocker'
  onSaved,                 // called with the updated profile object on success
}) {
  // Individual selectors — see teaminfo.jsx for context (whole-store destructure
  // contributed to the 429 cascade by re-rendering the modal on every store
  // update, which fires its own state-init useState defaults from a stale
  // profile reference).
  const profile = useProfileStore((s) => s.profile);
  const updateProfile = useProfileStore((s) => s.updateProfile);
  const strings = getStrings(profile?.u_preferred_language || 'en').focus_logistics;

  // Pre-fill from current profile so users editing later see their existing
  // selections, and so blocker-mode users who already partially answered (e.g.
  // u_customer_focus from a legacy code path) don't have to redo that field.
  const initialUserFocus = profile?.u_customer_focus || '';
  const initialBizFocus  = profile?.b_customer_focus ?? profile?.business?.b_customer_focus ?? '';
  const initialMiles     = profile?.b_service_distance ?? profile?.business?.b_service_distance ?? '';
  const initialZip       = profile?.b_zip_code ?? profile?.business?.b_zip_code ?? '';

  const [userFocus, setUserFocus] = useState(initialUserFocus);
  const [bizFocus, setBizFocus]   = useState(initialBizFocus);
  const [miles, setMiles]         = useState(initialMiles === null ? '' : String(initialMiles));
  const [zip, setZip]             = useState(initialZip || '');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Zip is locked when we already have one (from Google Places or a previous
  // save). Editable only when null/empty. Mirrors the old servicedistancestep
  // pattern so this feels familiar to existing-user testers, and prevents
  // accidental overwrites of the Places-verified address.
  const isZipLocked = Boolean(initialZip);

  if (!isOpen) return null;

  const isBlocker = mode === 'blocker';

  const handleSave = async () => {
    setError('');

    if (!userFocus || !bizFocus || !miles) {
      setError(strings.error_required);
      return;
    }
    const milesNum = Number(miles);
    if (!Number.isFinite(milesNum) || milesNum <= 0 || milesNum > 5000) {
      setError(strings.error_invalid_miles);
      return;
    }
    // Zip is required only when missing — locked-from-Places case already has one.
    if (!isZipLocked && !zip.trim()) {
      setError(strings.error_zip_required);
      return;
    }

    setSaving(true);
    try {
      const tok = localStorage.getItem('token');
      const res = await fetch('/api/profile/update-focus-and-logistics', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(tok && { Authorization: `Bearer ${tok}` }),
        },
        credentials: 'include',
        body: JSON.stringify({
          u_id: profile?.u_id,
          u_customer_focus: userFocus,
          b_customer_focus: bizFocus,
          b_service_distance: milesNum,
          // Only send a zip when we're providing a new one — locked case omits.
          // The backend ALSO refuses to overwrite a non-null zip server-side as
          // belt-and-suspenders, but the client clamps it here so the round-
          // trip is cleaner.
          ...(isZipLocked ? {} : { b_zip_code: zip.trim() }),
        }),
      });

      if (!res.ok) {
        let msg = '';
        try { msg = (await res.json())?.message || ''; } catch { /* not JSON */ }
        throw new Error(msg || `HTTP ${res.status}`);
      }

      const updated = await res.json();

      // Update Zustand. The endpoint returns the merged user+business shape,
      // but the profileStore expects business fields at both the top level
      // AND inside `business` for some legacy selectors — mirror to both so
      // getCustomerFocus / getServiceDistance both unblock immediately.
      updateProfile({
        u_customer_focus: updated.u_customer_focus,
        b_customer_focus: updated.b_customer_focus,
        b_service_distance: updated.b_service_distance,
        b_zip_code: updated.b_zip_code,
        business: {
          ...(profile?.business || {}),
          b_customer_focus: updated.b_customer_focus,
          b_service_distance: updated.b_service_distance,
          b_zip_code: updated.b_zip_code,
        },
      });

      onSaved?.(updated);
    } catch (err) {
      console.error('[FocusAndLogisticsModal] save failed:', err);
      setError(strings.error_save_failed(err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="focus-logistics-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      // Backdrop click closes ONLY in editor mode — blocker mode is, well, a blocker.
      onClick={isBlocker ? undefined : (e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '24px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
          position: 'relative',
        }}
      >
        {!isBlocker && (
          <button
            onClick={onClose}
            aria-label={strings.cancel}
            style={{
              position: 'absolute',
              top: '10px',
              right: '14px',
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}

        <h2
          id="focus-logistics-title"
          style={{ fontSize: '18px', fontWeight: 700, color: '#222', margin: '0 0 6px 0' }}
        >
          {isBlocker ? strings.blocker_title : strings.editor_title}
        </h2>
        <p style={{ fontSize: '13px', color: '#555', margin: '0 0 20px 0', lineHeight: 1.45 }}>
          {isBlocker ? strings.blocker_subtitle : strings.editor_subtitle}
        </p>

        {/* Q1: Business focus */}
        <FocusQuestion
          legend={strings.q_biz_focus}
          name="biz-focus"
          value={bizFocus}
          onChange={(next) => {
            setBizFocus(next);
            // Cascade rule (Brett 2026-05-26): if the COMPANY only serves one
            // side (B2B or B2C), the user's personal focus has to be the same —
            // a Falang-only tanning studio can't have a B2B-focused individual
            // employee. So we mirror + lock the user question. Only B2BC
            // leaves the user question fully editable since the company
            // serves both, and an individual can specialize either way.
            if (next === 'B2B' || next === 'B2C') {
              setUserFocus(next);
            }
          }}
          strings={strings}
        />

        {/* Q2: User personal focus — auto-mirrored + locked when biz is B2B/B2C */}
        <FocusQuestion
          legend={strings.q_user_focus}
          name="user-focus"
          value={userFocus}
          onChange={setUserFocus}
          strings={strings}
          disabled={bizFocus === 'B2B' || bizFocus === 'B2C'}
        />

        {/* Q3: Service distance + zip — mirrors the old servicedistancestep
            layout so existing-flow users see familiar UI. Zip is locked when
            Google Places verified one at signup; editable otherwise. */}
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px 0' }}>
          <legend style={{ fontWeight: 600, fontSize: '14px', color: '#222', marginBottom: '8px', padding: 0 }}>
            {strings.q_service_distance}
          </legend>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="number"
              min="1"
              max="5000"
              placeholder="30"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
              style={{
                width: '80px',
                padding: '8px 10px',
                border: '1px solid #ccc',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <span style={{ fontSize: '14px', color: '#555' }}>{strings.miles_label}</span>
            <input
              type="text"
              placeholder={strings.zip_placeholder}
              value={zip}
              onChange={(e) => !isZipLocked && setZip(e.target.value)}
              readOnly={isZipLocked}
              style={{
                width: '120px',
                padding: '8px 10px',
                border: `1px solid ${isZipLocked ? '#ccc' : '#999'}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: isZipLocked ? '#f5f5f5' : '#fff',
                color: isZipLocked ? '#666' : '#000',
                cursor: isZipLocked ? 'not-allowed' : 'text',
              }}
            />
          </div>
          {isZipLocked && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '6px', fontStyle: 'italic' }}>
              {strings.zip_locked_hint}
            </div>
          )}
        </fieldset>

        {error && (
          <div
            role="alert"
            style={{
              background: '#fde8e8',
              color: '#9b1c1c',
              padding: '8px 12px',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '13px',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {!isBlocker && (
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '9px 18px',
                background: 'transparent',
                color: '#555',
                border: '1px solid #ccc',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {strings.cancel}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '9px 22px',
              background: saving ? '#7fd5fa' : BRAND,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            {saving ? strings.saving : strings.save}
          </button>
        </div>
      </div>
    </div>
  );
}

// Single radio-group question. Three options: B2C, B2B, B2BC. We expose B2C
// first because the visual order tends to mirror "consumers → businesses →
// both," which Brett wrote into the spec.
//
// `disabled` prop (added 2026-05-26 for the B2B/B2C cascade): when true,
// the group is read-only — radios can't be changed and visual weight drops
// to communicate "this is locked because of an upstream answer." Used by the
// user-focus question when company-focus is B2B or B2C (cascade rule).
function FocusQuestion({ legend, name, value, onChange, strings, disabled = false }) {
  const options = [
    { val: 'B2C',  label: strings.opt_b2c },
    { val: 'B2B',  label: strings.opt_b2b },
    { val: 'B2BC', label: strings.opt_b2bc },
  ];
  return (
    <fieldset
      style={{ border: 'none', padding: 0, margin: '0 0 20px 0', opacity: disabled ? 0.55 : 1 }}
      disabled={disabled}
      aria-disabled={disabled || undefined}
    >
      <legend style={{ fontWeight: 600, fontSize: '14px', color: '#222', marginBottom: '8px', padding: 0 }}>
        {legend}
      </legend>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {options.map((opt) => (
          <label
            key={opt.val}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              background: value === opt.val ? 'rgba(0, 174, 247, 0.08)' : 'transparent',
              border: value === opt.val ? `1px solid ${BRAND}` : '1px solid transparent',
              fontSize: '14px',
              color: '#333',
            }}
          >
            <input
              type="radio"
              name={name}
              value={opt.val}
              checked={value === opt.val}
              onChange={() => !disabled && onChange(opt.val)}
              disabled={disabled}
              style={{ accentColor: BRAND }}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
